import { useSiteText } from "../i18n";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { IconName } from "../data/types";

export function ButtonLink({
  to,
  children,
  secondary = false,
  arrow = true,
  className = "",
}: {
  to: string;
  children: ReactNode;
  secondary?: boolean;
  arrow?: boolean;
  className?: string;
}) {
  const txt = useSiteText();
  return (
    <Link
      to={to}
      className={`button ${secondary ? "button--secondary" : ""} ${className}`}
    >
      {txt(children)}
      {arrow && <Icon name="arrow" size={18} />}
    </Link>
  );
}
export function TextLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  const txt = useSiteText();
  return (
    <Link className="text-link" to={to}>
      {txt(children)}
      <Icon name="arrow" size={19} />
    </Link>
  );
}
export function SectionHeading({
  eyebrow,
  title,
  children,
  centered = false,
}: {
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  centered?: boolean;
}) {
  const txt = useSiteText();
  return (
    <div className={`section-heading ${centered ? "centered" : ""}`}>
      {eyebrow && <p className="eyebrow">{txt(eyebrow)}</p>}
      <h2>{txt(title)}</h2>
      {children && <div className="section-description">{txt(children)}</div>}
    </div>
  );
}
export function PageHero({
  eyebrow,
  title,
  description,
  children,
  visual,
  className = "",
}: {
  eyebrow: string;
  title: string;
  description: string;
  children?: ReactNode;
  visual?: ReactNode;
  className?: string;
}) {
  const txt = useSiteText();
  return (
    <section
      className={`page-hero container ${visual ? "page-hero--split" : ""} ${className}`}
    >
      <div className="page-hero-copy">
        <p className="eyebrow">{txt(eyebrow)}</p>
        <h1>{txt(title)}</h1>
        <p className="lead">{txt(description)}</p>
        {children && <div className="hero-actions">{txt(children)}</div>}
      </div>
      {visual && <div className="page-hero-visual">{txt(visual)}</div>}
    </section>
  );
}
export function TrustStrip() {
  const txt = useSiteText();
  const items: [IconName, string][] = [
    ["clock", "Available when needed"],
    ["spark", "Personalized to you"],
    ["shield", "Privacy-aware"],
    ["people", "Alongside human support"],
  ];
  return (
    <div className="trust-strip container">
      {items.map(([icon, label]) => (
        <div key={label}>
          <Icon name={icon} size={20} />
          <span>{txt(label)}</span>
        </div>
      ))}
    </div>
  );
}
export function CTABand({
  title = "Human life is continuous. Support should be too.",
  description = "Find support for your everyday life, or explore what Systein could make possible for the people you serve.",
  organization = false,
  primaryTo,
  primaryLabel,
}: {
  title?: string;
  description?: string;
  organization?: boolean;
  primaryTo?: string;
  primaryLabel?: string;
}) {
  const txt = useSiteText();
  return (
    <section className="cta-band container">
      <div>
        <p className="eyebrow">{txt("A little support can go a long way")}</p>
        <h2>{txt(title)}</h2>
        <p>{txt(description)}</p>
      </div>
      <div className="cta-band-actions">
        <ButtonLink
          to={
            primaryTo ||
            (organization ? "/contact?interest=demo" : "/solutions/individuals")
          }
        >
          {txt(
            primaryLabel || (organization ? "Book a Demo" : "Explore Systein"),
          )}
        </ButtonLink>
        <TextLink
          to={organization ? "/platform/safety" : "/contact?interest=demo"}
        >
          {txt(organization ? "Safety & privacy" : "For organizations")}
        </TextLink>
      </div>
    </section>
  );
}
export function FeatureList({ items }: { items: [string, string][] }) {
  const txt = useSiteText();
  return (
    <div className="feature-list">
      {items.map(([title, text], i) => (
        <article key={title}>
          <span className="feature-number">0{i + 1}</span>
          <div>
            <h3>{txt(title)}</h3>
            <p>{txt(text)}</p>
          </div>
        </article>
      ))}
    </div>
  );
}
export function Boundary({ children }: { children: ReactNode }) {
  const txt = useSiteText();
  return (
    <aside className="boundary">
      <Icon name="shield" size={22} />
      <div>
        <strong>{txt("Support with clear boundaries.")}</strong>
        <p>{txt(children)}</p>
      </div>
    </aside>
  );
}
